import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { UserManagent } from '@/types';
import { useHeader } from '@/components/context/HeaderContext';
import { DataTable } from '@/components/common/DataTable';
import { useGetUsersQuery } from '@/store/api/userApi';
import {
  fetchPostHogAllUsers,
  IPostHogUserReport,
  fetchAllUsersEventsReport,
  IAllUsersEventReport,
  fetchPostHogAllGiftClicks,
  IGiftOneReport,
  fetchPostHogAllGetClicks,
  IGetOneReport,
} from '@/store/api/posthogAnalytics';
import { Eye } from 'lucide-react';
import { BlockUserButton } from './components/BlockUserButton';
import { ReportsDropdown } from './components/ReportsDropdown';

export default function UsersManagement() {
  const { setHeader } = useHeader();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [search, setSearch] = useState('');
  const skip = (page - 1) * take;

  const { data, isLoading } = useGetUsersQuery(
    {
      skip,
      take,
      search,
    },
    { refetchOnMountOrArgChange: true }
  );

  const [isExporting, setIsExporting] = useState(false);

  const convertToCSV = (users: IPostHogUserReport[]) => {
    const headers = ['Name', 'Email', 'User Tier', 'Created At'];
    const rows = users.map((user) => [
      user.name,
      user.email,
      user.userTier,
      user.createdAt ? new Date(user.createdAt).toLocaleString() : 'N/A',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((val) => {
            const str = String(val ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          })
          .join(',')
      ),
    ].join('\n');

    return csvContent;
  };

  const downloadCSV = (csvContent: string, fileName: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportCSV = async (filter: 'all' | 'premium' | 'free' = 'all') => {
    try {
      setIsExporting(true);
      const allUsers = await fetchPostHogAllUsers();

      let filteredUsers = allUsers;
      if (filter === 'premium') {
        filteredUsers = allUsers.filter((user) => user.userTier === 'premium');
      } else if (filter === 'free') {
        filteredUsers = allUsers.filter((user) => user.userTier !== 'premium');
      }

      const csv = convertToCSV(filteredUsers);

      let filename = 'users_posthog_report.csv';
      if (filter === 'premium') {
        filename = 'premium_users_posthog_report.csv';
      } else if (filter === 'free') {
        filename = 'free_users_posthog_report.csv';
      }

      downloadCSV(csv, filename);
    } catch (error) {
      console.error('Failed to export users from PostHog:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const [isExportingEvents, setIsExportingEvents] = useState(false);

  const convertEventsToCSV = (events: IAllUsersEventReport[]) => {
    const headers = ['Name', 'Email', 'Event Description', 'Timestamp'];
    const rows = events.map((ev) => [ev.name, ev.email, ev.event, ev.timestamp]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((val) => {
            const str = String(val ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          })
          .join(',')
      ),
    ].join('\n');

    return csvContent;
  };

  const handleExportUserEventsCSV = async () => {
    try {
      setIsExportingEvents(true);
      const allEvents = await fetchAllUsersEventsReport();
      const csv = convertEventsToCSV(allEvents);
      downloadCSV(csv, 'all_user_events_report.csv');
    } catch (error) {
      console.error('Failed to export all user events:', error);
    } finally {
      setIsExportingEvents(false);
    }
  };

  const [isExportingGiftClicks, setIsExportingGiftClicks] = useState(false);

  const convertGiftClicksToCSV = (clicks: IGiftOneReport[]) => {
    const headers = ['Name', 'Email', 'Mantra Name', 'Button Link', 'Clicked At'];
    const rows = clicks.map((click) => [
      click.name,
      click.email,
      click.mantraName,
      click.link,
      click.timestamp ? new Date(click.timestamp).toLocaleString() : 'N/A',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((val) => {
            const str = String(val ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          })
          .join(',')
      ),
    ].join('\n');

    return csvContent;
  };

  const handleExportGiftClicksCSV = async () => {
    try {
      setIsExportingGiftClicks(true);
      const allClicks = await fetchPostHogAllGiftClicks();
      const csv = convertGiftClicksToCSV(allClicks);
      downloadCSV(csv, 'all_gift_clicks_report.csv');
    } catch (error) {
      console.error('Failed to export all gift clicks:', error);
    } finally {
      setIsExportingGiftClicks(false);
    }
  };

  const [isExportingGetClicks, setIsExportingGetClicks] = useState(false);

  const convertGetClicksToCSV = (clicks: IGetOneReport[]) => {
    const headers = ['Name', 'Email', 'Mantra Name', 'Button Link', 'Clicked At'];
    const rows = clicks.map((click) => [
      click.name,
      click.email,
      click.mantraName,
      click.link,
      click.timestamp ? new Date(click.timestamp).toLocaleString() : 'N/A',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((val) => {
            const str = String(val ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          })
          .join(',')
      ),
    ].join('\n');

    return csvContent;
  };

  const handleExportGetClicksCSV = async () => {
    try {
      setIsExportingGetClicks(true);
      const allClicks = await fetchPostHogAllGetClicks();
      const csv = convertGetClicksToCSV(allClicks);
      downloadCSV(csv, 'all_get_clicks_report.csv');
    } catch (error) {
      console.error('Failed to export all get clicks:', error);
    } finally {
      setIsExportingGetClicks(false);
    }
  };

  useEffect(() => {
    setHeader({
      type: 'user',
      props: {
        title: 'User Management',
        lastUpdated: '14-April-2026, 3:00PM',
        showSort: true,
        showFilter: true,
        showMore: true,
      },
    });
    return () => setHeader(null);
  }, [setHeader]);

  const handleViewUserLogs = useCallback(
    (user: UserManagent) => {
      navigate(`/user-management/${user.id}/logs`, {
        state: {
          email: user.email,
          fullName: user.fullName,
        },
      });
    },
    [navigate]
  );

  const userColumns = useMemo<ColumnDef<UserManagent>[]>(
    () => [
      {
        accessorKey: 'fullName',
        header: 'Name',
        cell: ({ row }) => (
          <div className="font-semibold text-[#2E1A47] text-sm break-words min-w-[150px]">
            {row.getValue('fullName')}
          </div>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => <div className="text-[#2E1A47] text-sm">{row.getValue('email')}</div>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.getValue('status') as string;
          const isActive = status === 'Active';

          return (
            <span
              className={`px-4 py-1 rounded-full text-xs font-medium ${
                isActive ? 'bg-[#FFF5D9] text-[#D4A106]' : 'bg-gray-200 text-gray-600'
              }`}
            >
              {status}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Reports',
        cell: ({ row }) => {
          const user = row.original;
          return (
            <button
              onClick={() => handleViewUserLogs(user)}
              className="p-2 text-[#8B5CF6] hover:bg-violet-500/10 rounded-full transition-colors cursor-pointer"
              title="View listening logs"
            >
              <Eye className="w-5 h-5" />
            </button>
          );
        },
      },
      {
        id: 'blockUnblock',
        header: 'Block / Unblock',
        cell: ({ row }) => {
          const user = row.original;
          return <BlockUserButton user={user} />;
        },
      },
    ],
    [handleViewUserLogs]
  );

  const handleTakeChange = useCallback((value: number) => {
    setTake(value);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  return (
    <div className="p-4 min-h-screen">
      <DataTable
        columns={userColumns}
        data={data?.data ?? []}
        title="Users"
        totalCount={data?.count ?? 0}
        isServerPaginated
        page={page}
        take={take}
        loading={isLoading}
        search={search}
        onPageChange={setPage}
        onTakeChange={handleTakeChange}
        enableSearch
        searchPlaceholder="Search by name"
        onSearchChange={handleSearchChange}
        extraActions={
          <ReportsDropdown
            isExporting={isExporting}
            isExportingEvents={isExportingEvents}
            isExportingGiftClicks={isExportingGiftClicks}
            isExportingGetClicks={isExportingGetClicks}
            handleExportCSV={handleExportCSV}
            handleExportUserEventsCSV={handleExportUserEventsCSV}
            handleExportGiftClicksCSV={handleExportGiftClicksCSV}
            handleExportGetClicksCSV={handleExportGetClicksCSV}
          />
        }
      />
    </div>
  );
}
